// File: src/pages/EventQuizPage.jsx
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LuArrowLeft, LuCheck } from 'react-icons/lu';
import {
  IoShieldCheckmarkOutline,
  IoChatbubblesOutline,
  IoTimeOutline,
  IoPeopleOutline,
} from 'react-icons/io5';
import { AuthContext } from '../context/AuthContext';
import EventHelpTooltip from '../components/EventPreview/EventHelpTooltip';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';
import mixerApi from '../utils/mixerApi';
import UnsavedChangesBottomSheet from '../components/UnsavedChangesBottomSheet';

export default function EventQuizPage() {
  const { eventId } = useParams();            // can be “mixer-<id>”
  const navigate    = useNavigate();
  const { state }   = useLocation();          // look for event in router state
  const { currentUser } = useContext(AuthContext);

  /* ---------- local state ---------- */
  const [loading,  setLoading]   = useState(true);
  const [questions,setQuestions] = useState([]);
  const [answers,  setAnswers]   = useState({});
  const [idx,      setIdx]       = useState(0);      // current question index
  const [error,    setError]     = useState('');
  const [event,    setEvent]     = useState(state?.event || null);

  // NEW: show confirm-exit sheet for the top back arrow
  const [showUnsavedSheet, setShowUnsavedSheet] = useState(false);

  /* ---------- fetch quiz (and event fallback) ---------- */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await mixerApi.fetchQuiz(
          eventId.replace('mixer-', ''),
          currentUser._id
        );
        if (!alive) return;
        setQuestions(data.items || []);
        setEvent(ev => ev || data.event || null); // keep existing if present
      } catch (err) {
        console.error('[QuizPage] fetch error', err);
        if (alive) setError('Could not load quiz – please try again.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [eventId, currentUser]);

  /* ---------- fetch event if quiz call didn’t include it ---------- */
  useEffect(() => {
    if (event) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/mixer/events/${eventId.replace('mixer-', '')}`
        );
        if (alive) setEvent(data);
      } catch (err) {
        console.error('[QuizPage] event fetch error', err);
      }
    })();
    return () => { alive = false; };
  }, [event, eventId]);

  /* ---------- helpers ---------- */
  const currentQ = questions[idx] || {};
  const answeredCurrent = currentQ?.id != null && answers[currentQ.id] != null;

  const handleAnswer = (val) =>
    setAnswers(prev => ({ ...prev, [currentQ.id]: val }));

  // TOP BACK ARROW: open confirm sheet always (regardless of idx)
  const handleTopBackClick = () => {
    setShowUnsavedSheet(true);
  };

  // BOTTOM BACK BUTTON: disabled on first step, go back a step otherwise
  const handleBack = () => {
    if (idx === 0) return; // inactive on first step
    setIdx(i => i - 1);
  };

  const handleNext = () => {
    if (!answeredCurrent) return;
    setIdx(i => Math.min(i + 1, questions.length - 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = questions.map(q => ({
        axis : q.axis,
        id   : q.id,
        value: answers[q.id],
      }));
      await mixerApi.submitQuizAnswers(
        eventId.replace('mixer-', ''),
        currentUser._id,
        payload
      );
      navigate(`/mixer/events/${eventId}`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Submit failed – try again.';
      console.error('[QuizPage] submit error', err);
      if (msg === 'Quiz already submitted.') {
        navigate(`/mixer/events/${eventId}`);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const stepText = useMemo(
    () => `Question ${Math.min(idx + 1, questions.length)} of ${questions.length || 0}`,
    [idx, questions.length]
  );

  const ANSWER_CHOICES = [
    { value: 1, label: '1 — Strongly disagree' },
    { value: 2, label: '2 — Disagree' },
    { value: 3, label: '3 — Neutral / Mixed feelings' },
    { value: 4, label: '4 — Agree' },
    { value: 5, label: '5 — Strongly agree' },
  ];

  /* ---------- render ---------- */
  if (loading && questions.length === 0) return <div style={{ padding: 20, fontFamily: 'Inter, system-ui' }}>Loading quiz…</div>;
  if (error)   return <div style={{ padding: 20, color: '#b91c1c', fontFamily: 'Inter, system-ui' }}>{error}</div>;

  return (
    <div style={S.app}>
      {/* ── Sticky header (matches updated pages) ───────────────── */}
      <header style={S.header}>
        <button style={S.backBtn} onClick={handleTopBackClick} aria-label="Go back">
          <LuArrowLeft size={16} />
        </button>

        <div style={S.logoWrap} aria-hidden="true">
          <TribeLogo style={S.logoSvg} />
        </div>

        <div style={S.rightWrap}>
          <EventHelpTooltip
            title="About the Quiz"
            body="Answer each prompt honestly. Your responses help us match you with the right people. You can’t skip a question."
          />
        </div>
      </header>

      {/* Page container */}
      <div style={S.page}>
        {/* Progress / Step – styled as a pill */}
        <div style={S.stepText}>{stepText}</div>

        {/* Question text */}
        <h1 style={S.questionText}>
          {currentQ?.text || '—'}
        </h1>

        {/* Answer options */}
        <div style={S.optionsWrap}>
          {ANSWER_CHOICES.map(({ value, label }) => {
            const checked = answers[currentQ.id] === value;
            return (
              <label
                key={value}
                htmlFor={`q-${currentQ.id}-${value}`}
                style={{
                  ...S.optionItem,
                  ...(checked ? S.optionItemActive : {}),
                }}
              >
                <input
                  id={`q-${currentQ.id}-${value}`}
                  type="radio"
                  name={`q-${currentQ.id}`}
                  value={value}
                  checked={checked}
                  onChange={() => handleAnswer(value)}
                  style={S.radioInput}
                />
                <span style={S.optionLabel}>{label}</span>
                {/* Right-side status bubble */}
                <span
                  aria-hidden
                  style={{
                    ...S.checkBubble,
                    ...(checked ? S.checkBubbleActive : {}),
                  }}
                >
                  {checked && <LuCheck size={12} color="#fff" />}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Sticky bottom actions */}
      <div style={S.buttonBar} role="group" aria-label="Quiz navigation">
        <div style={S.buttonRow}>
          <button
            onClick={handleBack}
            style={{
              ...S.backBtnBottom,
              ...(idx === 0 ? S.backBtnDisabled : {}),
            }}
            disabled={idx === 0}
          >
            Back
          </button>

          {idx < questions.length - 1 ? (
            <button
              onClick={handleNext}
              style={{
                ...S.nextBtn,
                ...(answeredCurrent ? {} : S.nextBtnDisabled),
              }}
              disabled={!answeredCurrent}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              style={{
                ...S.nextBtn,
                ...(answeredCurrent ? {} : S.nextBtnDisabled),
              }}
              disabled={!answeredCurrent || loading}
            >
              {loading ? 'Submitting…' : 'Submit'}
            </button>
          )}
        </div>
      </div>

      {/* Confirm-exit bottom sheet */}
      {showUnsavedSheet && (
        <UnsavedChangesBottomSheet
          onCancel={() => setShowUnsavedSheet(false)}
          onDiscard={() => {
            setShowUnsavedSheet(false);
            // Navigate back to Mixer Intro Wizard, preserving event in state
            if (event) navigate('/intro-mixer', { state: { event } });
            else navigate('/intro-mixer');
          }}
        />
      )}
    </div>
  );
}

/* -------------------- Styles (Inter-only, hairlines, black primary) -------------------- */
const COLOR = {
  ink: '#0B1020',
  sub: '#3A4050',
  line: '#E7E9EE',
  headerBg: '#FFFFFF',
  chip: '#F3F4F6',
  chipBorder: '#E5E7EB',
  black: '#0b0b0b',
};

const GRIDW = 'min(720px, 92vw)';

const S = {
  app: {
    fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    color: COLOR.ink,
    background: '#fff',
  },

  /* Header */
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%',
    maxWidth: 1200,
    margin: '0 auto',
    height: 56,
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr auto 1fr',
    background: COLOR.headerBg,
  },
  backBtn: {
    justifySelf: 'start',
    marginLeft: 12,
    background: COLOR.black,
    color: '#fff',
    border: 'none',
    borderRadius: 9999,
    width: 40,
    height: 26,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  logoWrap: {
    justifySelf: 'center',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  logoSvg: {
    width: 72,
    height: 'auto',
    display: 'block',
  },
  rightWrap: {
    justifySelf: 'end',
    marginRight: 12,
    display: 'flex',
    alignItems: 'center',
    height: 32,
  },

  /* Page container */
  page: {
    width: GRIDW,
    margin: '14px auto 120px',
    padding: 0,
  },

  /* Step pill look */
  stepText: {
    display: 'inline-block',
    background: COLOR.chip,
    border: `1px solid ${COLOR.chipBorder}`,
    color: COLOR.ink,
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1,
    margin: '6px 0 10px',
  },

  /* Question */
  questionText: {
    margin: '6px 0 10px',
    fontSize: 'clamp(1.35rem, 3.2vw, 1.9rem)',
    fontWeight: 900,
    letterSpacing: '-0.01em',
    lineHeight: 1.2,
  },

  /* Options */
  optionsWrap: {
    display: 'grid',
    gap: 10,
    marginTop: 6,
  },
  optionItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${COLOR.line}`,
    background: '#fff',
    boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background 120ms ease',
  },
  optionItemActive: {
    border: `1px solid ${COLOR.black}`,
    background: '#F7FAFF',
  },
  radioInput: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
  },
  optionLabel: {
    flex: 1,
    fontSize: 14.5,
    color: COLOR.ink,
  },
  checkBubble: {
    width: 20,
    height: 20,
    borderRadius: 999,
    border: `2px solid ${COLOR.line}`,
    background: '#fff',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  checkBubbleActive: {
    background: COLOR.black,
    border: `2px solid ${COLOR.black}`,
  },

  /* Bottom buttons — black primary, 10px radius */
  buttonBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '12px 20px calc(12px + env(safe-area-inset-bottom))',
    background: 'transparent',
    pointerEvents: 'none',
  },
  buttonRow: {
    pointerEvents: 'auto',
    margin: '0 auto',
    width: '100%',
    display: 'flex',
    gap: 10,
  },
  backBtnBottom: {
    flex: '0 0 32%',
    height: 44,
    borderRadius: 10,
    background: '#FFFFFF',
    color: COLOR.ink,
    border: `1px solid ${COLOR.line}`,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  backBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  nextBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    border: `1px solid ${COLOR.black}`,
    background: COLOR.black,
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  nextBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
};
